import type {
  ApiError,
  EarnResponse,
  QuizResponse,
  FillView,
  LoginChallengeRequest,
  LoginChallengeResponse,
  MeResponse,
  OfferView,
  RegisterWalletRequest,
  RewardsResponse,
  SendFeeSchedule,
  SignedBody,
  StatusResponse,
  UnsignedView,
  UpdateMeRequest,
  WalletActivityItem,
  WalletLoginRequest,
  WalletResponse,
} from '@xchange/shared';
import type { Platform } from './platform/index.js';

export interface ActivityItem {
  id: string;
  pair: string;
  side: 'sell' | 'buy';
  baseAmount: string;
  quoteAmount: string;
  kind: 'trade' | 'offer';
  at: string;
}

/** The public figures, as xchangehq.com/market reads them; no sign-in needed. */
export interface PublicMarket {
  updatedAt: string;
  pairs: {
    name: string;
    display: string;
    base: string;
    quote: string;
    priceUnit: string;
    /** The contracts' fee on each leg received, in basis points. */
    feeBps: number;
    /** The flat charge on a Solana order, in the asset deposited; absent from older servers. */
    orderFee?: { base: string | null; quote: string | null };
    trades24h: number;
    volume24h: string;
    lastPrice: string | null;
    price24hAgo: string | null;
    /** The five best levels a side, quote smallest units per price unit; base left. */
    asks?: { price: string; remaining: string }[];
    bids?: { price: string; remaining: string }[];
  }[];
  xge: { supply: string; rewardsWallet: string | null; paidOut: string; holders: number | null };
  gaju: {
    supply: string | null;
    height: number | null;
    at: string | null;
    holders: number | null;
    maxSupply: string;
  };
  usdcInBids: string;
  rewards?: {
    poolTodayXge: string;
    /** The day's XGE, in XGE minor units. */
    dailyXge?: string;
    /** The one true line about rewards, as the server words it; null when none run. */
    sharedLine?: string | null;
  };
}

export interface PairView {
  name: string;
  base: string;
  quote: string;
  priceUnit: string;
  priceUnitText: string;
  /** The base coin's total supply on its chain, in its smallest units; null until read. */
  baseSupply: string | null;
  /**
   * xchange's flat charge on a Solana order, in the asset the order deposits
   * (base for a sell, quote for a buy), in that asset's smallest units; null
   * when there is none (Gaju, or the charge is off).
   */
  orderFee: { base: string | null; quote: string | null };
  display: string;
  logo: 'GAJU' | 'USDC' | 'XGE';
  live: boolean;
  /** The contracts' fee on each leg received, in basis points. */
  feeBps: number;
  /** The last 24 hours: settled trades, volume in the quote asset's smallest units. */
  trades24h: number;
  volume24h: string;
  /** Quote smallest units per price unit; null before any trade. */
  lastPrice: string | null;
  price24hAgo: string | null;
  /** Testers may take their own offers, and see them under Buy and Sell. */
  testing?: boolean;
}

/** The self-custody side, for the admin. */
export interface AdminWalletPerson {
  id: string;
  name: string;
  username: string | null;
  telegramId: string;
  joinedAt: string;
  lastSeenAt: string | null;
  addresses: { solana: string | null; gajumaru: string | null };
  gaju: string;
  usdc: string;
  xge: string;
  trades: number;
  xgeRewards: string;
}
export interface UsageReport {
  days: number;
  devices: number;
  accounts: number;
  returning: number;
  funnel: { step: string; devices: number }[];
  features: { name: string; count: number; devices: number }[];
  daily: { day: string; devices: number }[];
  errors: { where: string; count: number }[];
  /** How the newcomer steps split by their small values, e.g. welcome_left by secs. */
  details?: { name: string; prop: string; value: string; devices: number }[];
}

export interface AdminWallets {
  total: number;
  activeToday: number;
  haveTraded: number;
  trades: number;
  holdings: { gaju: string; usdc: string; xge: string };
  rewardsPaid: string;
  people: AdminWalletPerson[];
}

export class RequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

const base = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

/**
 * The session, kept in memory and sent as a header. Inside Telegram Web the
 * app is a frame on another site, where the browser withholds our cookie; the
 * header is what makes the app work the same there as on a phone. Memory only:
 * every opening signs in afresh with what Telegram hands us.
 */
let session: string | null = null;

async function call<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
  keepalive = false,
): Promise<T> {
  const headers: Record<string, string> = {};
  if (session !== null) headers.authorization = `Bearer ${session}`;
  // keepalive lets a request finish after the tab is closed: used for the
  // last events of a visit, which are the ones that say where someone left.
  const init: RequestInit = { method, credentials: 'include', headers, keepalive };
  if (body !== undefined) {
    headers['content-type'] = 'application/json';
    init.body = JSON.stringify(body);
  }
  const res = await fetch(`${base}${path}`, init);
  const issued = res.headers.get('x-session-token');
  if (issued !== null && issued !== '') session = issued;
  const text = await res.text();
  let parsed: unknown = null;
  try {
    parsed = text === '' ? null : JSON.parse(text);
  } catch {
    /* not json */
  }
  if (!res.ok) {
    const err = (parsed ?? {}) as Partial<ApiError>;
    throw new RequestError(
      res.status,
      err.error ?? 'http',
      err.message ?? 'Something went wrong. Try again.',
    );
  }
  return parsed as T;
}

/**
 * A call that may hit the API while it restarts (every deploy, a few seconds)
 * or a phone whose network just dropped: tried again quietly for up to
 * twenty seconds before the person hears about it. Only for calls that are
 * safe to repeat.
 */
export async function patiently<T>(call: () => Promise<T>, ms = 20_000): Promise<T> {
  const until = Date.now() + ms;
  for (;;) {
    try {
      return await call();
    } catch (e) {
      const transient = !(e instanceof RequestError) || e.status >= 500;
      if (!transient || Date.now() > until) throw e;
      await new Promise((r) => setTimeout(r, 1500));
    }
  }
}

export const api = {
  status: () => call<StatusResponse>('GET', '/api/status'),
  signIn: (platform: Platform) => {
    const c = platform.credential();
    // A wallet signs in from its own screen; here only a session that is
    // still open counts, and a 401 means that screen is next.
    if (c.kind === 'wallet') return call<MeResponse>('GET', '/api/me');
    return c.kind === 'telegram'
      ? call<MeResponse>('POST', '/api/auth/telegram', { initData: c.initData })
      : call<MeResponse>('POST', '/api/auth/mock', {
          userId: c.userId,
          displayName: c.displayName,
          ...(c.botId === undefined ? {} : { botId: c.botId }),
        });
  },
  loginChallenge: (req: LoginChallengeRequest) =>
    call<LoginChallengeResponse>('POST', '/api/auth/challenge', req),
  loginWithWallet: (req: WalletLoginRequest) => call<MeResponse>('POST', '/api/auth/wallet', req),
  updateMe: (req: UpdateMeRequest) => call<MeResponse>('PATCH', '/api/me', req),
  me: () => call<MeResponse>('GET', '/api/me'),
  logout: () => call<{ ok: boolean }>('POST', '/api/auth/logout'),
  publicMarket: () => call<PublicMarket>('GET', '/api/public/market'),
  events: (body: {
    deviceId: string;
    platform: 'web' | 'telegram';
    events: { name: string; props?: Record<string, string | number | boolean>; at: string }[];
  }) => call<{ ok: true }>('POST', '/api/events', body, true),
  adminUsage: (days: number) => call<UsageReport>('GET', `/api/admin/usage?days=${days}`),
  wallet: () => call<WalletResponse>('GET', '/api/wallet'),
  pairs: () => call<PairView[]>('GET', '/api/pairs'),
  activity: () => call<ActivityItem[]>('GET', '/api/activity'),
  sendPrepare: (body: { asset: 'GAJU' | 'USDC' | 'XGE'; to: string; amount: string }) =>
    call<{ unsigned: UnsignedView; fee: string }>('POST', '/api/wallet/send/prepare', body),
  /** xchange's fee schedule on a send, per token; none for Gaju. */
  sendFees: () =>
    call<{ USDC: SendFeeSchedule; XGE: SendFeeSchedule; GAJU: null }>(
      'GET',
      '/api/wallet/send/fees',
    ),
  sendSubmit: (
    chain: 'gajumaru' | 'solana',
    body: SignedBody & { asset?: string; amount?: string },
  ) => call<{ hash: string; url: string }>('POST', '/api/wallet/send/submit', { chain, ...body }),
  rewards: () => call<RewardsResponse>('GET', '/api/rewards'),
  /** One answer to one of today's three questions on Earn. */
  quizAnswer: (questionId: string, choice: number) =>
    call<QuizResponse>('POST', '/api/quiz/answer', { questionId, choice }),
  /** The Earn screen, read once when it opens. */
  earn: () => call<EarnResponse>('GET', '/api/earn'),
  /** `pnpm try` only: count hours on the book now, or switch the farming rules. */
  dev: (what: 'coordinate' | 'rules', body: unknown) =>
    call<unknown>('POST', `/api/dev/${what}`, body),
  walletActivity: (asset: 'GAJU' | 'USDC' | 'XGE') =>
    call<WalletActivityItem[]>('GET', `/api/wallet/activity/${asset}`),
  offers: (pair?: string) =>
    call<{ open: OfferView[]; mine: OfferView[]; gajumaru?: string }>(
      'GET',
      `/api/offers${pair === undefined ? '' : `?pair=${pair}`}`,
    ),
  /** Your finished orders, the latest 200. Asked when History is looked at, never polled. */
  history: () => call<{ mine: OfferView[] }>('GET', '/api/history'),
  createOffer: (body: { pair: string; side: 'sell' | 'buy'; baseAmount: string; price: string }) =>
    call<{ id: string; unsigned: UnsignedView }>('POST', '/api/offers', body),
  submitOffer: (id: string, body: SignedBody) =>
    call<OfferView>('POST', `/api/offers/${id}/submit`, body),
  takeOffer: (id: string, baseAmount?: string) =>
    call<{ fillId: string; unsigned: UnsignedView }>(
      'POST',
      `/api/offers/${id}/take`,
      baseAmount === undefined ? {} : { baseAmount },
    ),
  submitTake: (fillId: string, body: SignedBody) =>
    call<FillView>('POST', `/api/offers/fills/${fillId}/submit`, body),
  untake: (fillId: string) => call<FillView>('POST', `/api/offers/fills/${fillId}/untake`),
  cancelOffer: (id: string) =>
    call<{ unsigned: UnsignedView | null }>('POST', `/api/offers/${id}/cancel`),
  submitCancel: (id: string, body: SignedBody) =>
    call<OfferView>('POST', `/api/offers/${id}/cancel/submit`, body),
  registerEscrow: (chain: 'gajumaru' | 'solana') =>
    call<{ registered: boolean; unsigned?: UnsignedView }>('POST', '/api/escrow/register', {
      chain,
    }),
  submitRegister: (chain: 'gajumaru' | 'solana', body: SignedBody) =>
    call<{ hash: string }>('POST', '/api/escrow/register/submit', { chain, ...body }),
  /** Something that failed on the phone, where the server would never see it. */
  reportClientError: (where: string, message: string) =>
    call<{ ok: true }>('POST', '/api/client-error', {
      where,
      message,
      agent: navigator.userAgent,
    }),
  registerWallet: (body: RegisterWalletRequest) =>
    call<WalletResponse>('POST', '/api/wallet/register', body),
  adminWallets: () => call<AdminWallets>('GET', '/api/admin/wallets'),
  adminWalletActivity: (id: string) =>
    call<(WalletActivityItem & { asset: 'GAJU' | 'USDC' | 'XGE' })[]>(
      'GET',
      `/api/admin/wallets/${id}/activity`,
    ),
};
